<!-- HomeV3.svelte - New Landing Page -->

<script>
  import { onMount } from "svelte";
  import { push } from 'svelte-spa-router';

  let heroVisible = $state(false);
  let sectionsVisible = $state({});

  function goToFocalPath() {
    push('/DeciderApp');
  }

  function setupIntersectionObserver() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionId = entry.target.dataset.section;
            sectionsVisible[sectionId] = true;
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    // Observe all sections with data-section attribute
    document.querySelectorAll('[data-section]').forEach(section => {
      observer.observe(section);
    });

    return observer;
  }

  onMount(() => {
    // Animate hero section
    setTimeout(() => {
      heroVisible = true;
    }, 300);

    // Setup intersection observer for other sections
    const observer = setupIntersectionObserver();

    return () => {
      observer.disconnect();
    };
  });
</script>

<main class="landing-page">
  <!-- 1. Hero Section -->
  <section class="hero-section" class:visible={heroVisible}>
    <div class="hero-content">
      <div class="hero-visual">
        <div class="confusion-to-clarity">
          <div class="flow-step confusion">
            <div class="icon">🤔</div>
            <span>Confusion</span>
          </div>
          <div class="arrow">→</div>
          <div class="flow-step structure">
            <div class="icon">🧭</div>
            <span>Structure</span>
          </div>
          <div class="arrow">→</div>
          <div class="flow-step clarity">
            <div class="icon">✨</div>
            <span>Clarity</span>
          </div>
        </div>
      </div>
      
      <h1 class="hero-headline">
        Make confident, science-backed decisions in just 10 minutes
      </h1>
      
      <p class="hero-subheadline">
        Focal Path turns overwhelming trade-offs into clear, data-driven paths — helping professionals and business owners move forward without doubt or regret.
      </p>
      
      <button class="primary-cta" onclick={goToFocalPath}>
        Start My First Decision
      </button>
      
      <p class="cta-tagline">
        Built on 50+ studies in decision science.
      </p>
    </div>
  </section>

  <!-- 2. Problem Section -->
  <section class="problem-section" data-section="problem" class:visible={sectionsVisible.problem}>
    <div class="container">
      <h2>When every choice feels like a gamble, progress stops.</h2>
      
      <div class="problem-content">
        <p>You've been there — second-guessing a career move, a hiring decision, a partnership.</p>
        <p>We call it <strong>decision fatigue</strong>. Endless pros and cons lists, gut feelings that shift daily, advice that only adds noise.</p>
        <div class="mini-hook">
          <p><em>It's not your fault — your brain isn't wired for modern complexity.</em></p>
        </div>
      </div>
    </div>
  </section>

  <!-- 3. Solution Section -->
  <section class="solution-section" data-section="solution" class:visible={sectionsVisible.solution}>
    <div class="container">
      <h2>Focal Path brings the science of great decisions to your screen.</h2>
      
      <div class="how-it-works">
        <div class="step">
          <div class="step-number">1</div>
          <div class="step-content">
            <h3>Define your challenge</h3>
            <p>Pick a template (career, business, life).</p>
          </div>
        </div>
        
        <div class="step">
          <div class="step-number">2</div>
          <div class="step-content">
            <h3>Structure your reasoning</h3>
            <p>Our guided prompts and weighting system reveal what truly matters.</p>
          </div>
        </div>
        
        <div class="step">
          <div class="step-number">3</div>
          <div class="step-content">
            <h3>See your clarity score</h3>
            <p>Visual dashboards show your best-fit option and potential biases.</p>
          </div>
        </div>
      </div>
      
      <button class="secondary-cta" onclick={goToFocalPath}>
        Try your first clarity session free
      </button>
    </div>
  </section>

  <!-- 4. Proof Section -->
  <section class="proof-section" data-section="proof" class:visible={sectionsVisible.proof}>
    <div class="container">
      <h2>Grounded in behavioural science — proven 6× more effective.</h2>
      
      <div class="proof-points">
        <div class="proof-item">
          <div class="proof-icon">🏢</div>
          <p>Based on frameworks used by McKinsey and leading decision researchers.</p>
        </div>
        
        <div class="proof-item">
          <div class="proof-icon">🧠</div>
          <p>Inspired by behavioural economics, not intuition.</p>
        </div>
        
        <div class="proof-item">
          <div class="proof-icon">📊</div>
          <p>Tested on real-world cases — from career switches to business strategy.</p>
        </div>
      </div>
      
      <div class="testimonial">
        <blockquote>
          "What makes a decision great is not that it has a great outcome. A great decision is the result of a good process."
        </blockquote>
        <cite>— Decision Science Research</cite>
      </div>
    </div>
  </section>

  <!-- 5. Speed & Ease Section -->
  <section class="speed-section" data-section="speed" class:visible={sectionsVisible.speed}>
    <div class="container">
      <h2>Clarity before your next coffee break.</h2>
      
      <div class="speed-benefits">
        <div class="benefit">
          <div class="benefit-icon">⏱️</div>
          <p>Takes 10 minutes to reach structured insight.</p>
        </div>
        
        <div class="benefit">
          <div class="benefit-icon">🎯</div>
          <p>No coaching calls, no long courses — just guided clarity.</p>
        </div>
        
        <div class="benefit">
          <div class="benefit-icon">🔒</div>
          <p>Simple, visual, and private.</p>
        </div>
      </div>
    </div>
  </section>

  <!-- 6. Pricing Section -->
  <section class="pricing-section" data-section="pricing" class:visible={sectionsVisible.pricing}>
    <div class="container">
      <h2>Choose your path to better decisions.</h2>
      
      <div class="pricing-grid">
        <div class="pricing-card free">
          <h3>Free Starter</h3>
          <div class="price">Free</div>
          <ul class="features">
            <li>Test one decision</li>
            <li>Basic clarity flow</li>
          </ul>
          <button class="plan-button" onclick={goToFocalPath}>Get Started</button>
        </div>
        
        <div class="pricing-card pro featured">
          <div class="popular-badge">Most Popular</div>
          <h3>Pro</h3>
          <div class="price">€12<span>/month</span></div>
          <p class="plan-subtitle">Independent professionals</p>
          <ul class="features">
            <li>Unlimited decisions</li>
            <li>Bias checks</li>
            <li>AI summaries</li>
          </ul>
          <button class="plan-button primary" onclick={goToFocalPath}>Start Pro Trial</button>
        </div>
        
        <div class="pricing-card executive">
          <h3>Executive</h3>
          <div class="price">€39<span>/month</span></div>
          <p class="plan-subtitle">Leaders & business owners</p>
          <ul class="features">
            <li>Scenario simulator</li>
            <li>Export reports</li>
            <li>Advanced analytics</li>
          </ul>
          <button class="plan-button" onclick={goToFocalPath}>Contact Sales</button>
        </div>
        
        <div class="pricing-card teams">
          <h3>Teams</h3>
          <div class="price">Custom</div>
          <p class="plan-subtitle">Small business use</p>
          <ul class="features">
            <li>Shared dashboards</li>
            <li>Group decisions</li>
            <li>Team analytics</li>
          </ul>
          <button class="plan-button" onclick={goToFocalPath}>Contact Us</button>
        </div>
      </div>
      
      <div class="pricing-guarantee">
        <p>Cancel anytime • 30-day money-back guarantee</p>
      </div>
    </div>
  </section>

  <!-- 7. Final CTA Section -->
  <section class="final-cta-section" data-section="final-cta" class:visible={sectionsVisible['final-cta']}>
    <div class="container">
      <h2>Stop overthinking. Start deciding.</h2>
      <button class="final-cta-button" onclick={goToFocalPath}>
        Start My Free Decision Now
      </button>
      <p class="final-cta-subtitle">
        Clarity, confidence, and control — in one simple session.
      </p>
    </div>
  </section>

  <!-- 8. Footer -->
  <footer class="footer" data-section="footer" class:visible={sectionsVisible.footer}>
    <div class="container">
      <div class="footer-content">
        <div class="footer-main">
          <p class="footer-tagline">
            Built by decision-science researchers and product designers.
          </p>
          
          <div class="footer-links">
            <a href="mailto:hello@focalpath.com">Contact</a>
            <a href="/privacy">Privacy</a>
            <a href="/terms">Terms</a>
          </div>
        </div>
        
        <div class="newsletter">
          <h4>Stay updated</h4>
          <div class="newsletter-form">
            <input type="email" placeholder="Enter your email" />
            <button type="submit">Subscribe</button>
          </div>
        </div>
      </div>
      
      <div class="footer-bottom">
        <p>&copy; 2024 Focal Path. All rights reserved.</p>
      </div>
    </div>
  </footer>
</main>

<style>
  /* Reset and base styles */
  .landing-page {
    font-family: 'IBM Plex Mono', monospace;
    line-height: 1.6;
    color: #333;
    overflow-x: hidden;
  }

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
  }

  /* Hero Section */
  .hero-section {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    text-align: center;
    position: relative;
    opacity: 0;
    transform: translateY(30px);
    transition: all 0.8s ease-out;
  }

  .hero-section.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .hero-content {
    max-width: 800px;
    padding: 2rem;
  }

  .confusion-to-clarity {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 3rem;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .flow-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    animation: fadeInUp 0.6s ease-out forwards;
  }

  .flow-step:nth-child(1) { animation-delay: 0.2s; }
  .flow-step:nth-child(3) { animation-delay: 0.4s; }
  .flow-step:nth-child(5) { animation-delay: 0.6s; }

  .flow-step .icon {
    font-size: 2.5rem;
    margin-bottom: 0.5rem;
  }

  .arrow {
    font-size: 1.5rem;
    opacity: 0.7;
    animation: fadeIn 0.6s ease-out 0.8s forwards;
    opacity: 0;
  }

  .hero-headline {
    font-size: 3.5rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    line-height: 1.2;
    animation: fadeInUp 0.8s ease-out 0.4s forwards;
    opacity: 0;
  }

  .hero-subheadline {
    font-size: 1.3rem;
    margin-bottom: 2.5rem;
    opacity: 0.9;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
    animation: fadeInUp 0.8s ease-out 0.6s forwards;
    opacity: 0;
  }

  .primary-cta {
    background: #9cfffa;
    color: #084b83;
    border: none;
    padding: 1.2rem 2.5rem;
    font-size: 1.2rem;
    font-weight: 600;
    border-radius: 50px;
    cursor: pointer;
    transition: all 0.3s ease;
    animation: fadeInUp 0.8s ease-out 0.8s forwards;
    opacity: 0;
    box-shadow: 0 4px 15px rgba(156, 255, 250, 0.3);
  }

  .primary-cta:hover {
    background: #ffec6c;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(156, 255, 250, 0.4);
  }

  .cta-tagline {
    margin-top: 1rem;
    font-size: 0.9rem;
    opacity: 0.8;
    animation: fadeInUp 0.8s ease-out 1s forwards;
    opacity: 0;
  }

  /* Section base styles */
  section {
    padding: 5rem 0;
    opacity: 0;
    transform: translateY(30px);
    transition: all 0.8s ease-out;
  }

  section.visible {
    opacity: 1;
    transform: translateY(0);
  }

  section h2 {
    font-size: 2.5rem;
    font-weight: 700;
    text-align: center;
    margin-bottom: 3rem;
    color: #333;
  }

  /* Problem Section */
  .problem-section {
    background: #f8f9fa;
  }

  .problem-content {
    max-width: 700px;
    margin: 0 auto;
    text-align: center;
  }

  .problem-content p {
    font-size: 1.2rem;
    margin-bottom: 1.5rem;
    color: #555;
  }

  .mini-hook {
    background: white;
    padding: 2rem;
    border-radius: 15px;
    margin-top: 2rem;
    box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  }

  .mini-hook p {
    margin: 0;
    font-size: 1.1rem;
    color: #667eea;
  }

  /* Solution Section */
  .how-it-works {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 3rem;
    margin-bottom: 3rem;
  }

  .step {
    display: flex;
    align-items: flex-start;
    gap: 1.5rem;
    padding: 2rem;
    background: white;
    border-radius: 15px;
    box-shadow: 0 4px 15px rgba(0,0,0,0.1);
    transition: transform 0.3s ease;
  }

  .step:hover {
    transform: translateY(-5px);
  }

  .step-number {
    background: #667eea;
    color: white;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 1.2rem;
    flex-shrink: 0;
  }

  .step-content h3 {
    margin: 0 0 0.5rem 0;
    font-size: 1.3rem;
    color: #333;
  }

  .step-content p {
    margin: 0;
    color: #666;
  }

  .secondary-cta {
    background: transparent;
    color: #667eea;
    border: 2px solid #667eea;
    padding: 1rem 2rem;
    font-size: 1.1rem;
    border-radius: 50px;
    cursor: pointer;
    transition: all 0.3s ease;
    display: block;
    margin: 0 auto;
  }

  .secondary-cta:hover {
    background: #667eea;
    color: white;
  }

  /* Proof Section */
  .proof-section {
    background: #1e1e1e;
    color: white;
  }

  .proof-section h2 {
    color: white;
  }

  .proof-points {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
    margin-bottom: 3rem;
  }

  .proof-item {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.5rem;
    background: rgba(255,255,255,0.1);
    border-radius: 10px;
  }

  .proof-icon {
    font-size: 2rem;
    flex-shrink: 0;
  }

  .testimonial {
    max-width: 600px;
    margin: 0 auto;
    text-align: center;
    padding: 2rem;
    background: rgba(255,255,255,0.1);
    border-radius: 15px;
  }

  .testimonial blockquote {
    font-size: 1.2rem;
    font-style: italic;
    margin: 0 0 1rem 0;
  }

  .testimonial cite {
    opacity: 0.8;
  }

  /* Speed Section */
  .speed-benefits {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 2rem;
  }

  .benefit {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 2rem;
    background: white;
    border-radius: 15px;
    box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  }

  .benefit-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
  }

  /* Pricing Section */
  .pricing-section {
    background: #f8f9fa;
  }

  .pricing-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 2rem;
    margin-bottom: 2rem;
  }

  .pricing-card {
    background: white;
    border-radius: 15px;
    padding: 2.5rem 2rem;
    text-align: center;
    box-shadow: 0 4px 15px rgba(0,0,0,0.1);
    position: relative;
    transition: transform 0.3s ease;
  }

  .pricing-card:hover {
    transform: translateY(-5px);
  }

  .pricing-card.featured {
    border: 3px solid #667eea;
    transform: scale(1.05);
  }

  .popular-badge {
    position: absolute;
    top: -10px;
    left: 50%;
    transform: translateX(-50%);
    background: #667eea;
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.9rem;
    font-weight: 600;
  }

  .pricing-card h3 {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
    color: #333;
  }

  .price {
    font-size: 2.5rem;
    font-weight: 700;
    color: #667eea;
    margin-bottom: 0.5rem;
  }

  .price span {
    font-size: 1rem;
    color: #666;
  }

  .plan-subtitle {
    color: #666;
    margin-bottom: 1.5rem;
    font-size: 0.9rem;
  }

  .features {
    list-style: none;
    padding: 0;
    margin: 0 0 2rem 0;
  }

  .features li {
    padding: 0.5rem 0;
    color: #666;
  }

  .features li:before {
    content: "✓";
    color: #667eea;
    font-weight: bold;
    margin-right: 0.5rem;
  }

  .plan-button {
    width: 100%;
    padding: 1rem;
    border: 2px solid #667eea;
    background: transparent;
    color: #667eea;
    border-radius: 50px;
    cursor: pointer;
    transition: all 0.3s ease;
    font-weight: 600;
  }

  .plan-button.primary {
    background: #667eea;
    color: white;
  }

  .plan-button:hover {
    background: #667eea;
    color: white;
  }

  .pricing-guarantee {
    text-align: center;
    color: #666;
    font-size: 0.9rem;
  }

  /* Final CTA Section */
  .final-cta-section {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    text-align: center;
  }

  .final-cta-section h2 {
    color: white;
    font-size: 3rem;
  }

  .final-cta-button {
    background: #9cfffa;
    color: #084b83;
    border: none;
    padding: 1.5rem 3rem;
    font-size: 1.3rem;
    font-weight: 700;
    border-radius: 50px;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-bottom: 1rem;
    box-shadow: 0 4px 20px rgba(156, 255, 250, 0.3);
  }

  .final-cta-button:hover {
    background: #ffec6c;
    transform: translateY(-3px);
    box-shadow: 0 6px 25px rgba(156, 255, 250, 0.4);
  }

  .final-cta-subtitle {
    font-size: 1.1rem;
    opacity: 0.9;
  }

  /* Footer */
  .footer {
    background: #1e1e1e;
    color: white;
    padding: 3rem 0 1rem;
  }

  .footer-content {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 3rem;
    margin-bottom: 2rem;
  }

  .footer-tagline {
    font-size: 1.1rem;
    margin-bottom: 1.5rem;
  }

  .footer-links {
    display: flex;
    gap: 2rem;
  }

  .footer-links a {
    color: #9cfffa;
    text-decoration: none;
    transition: opacity 0.3s ease;
  }

  .footer-links a:hover {
    opacity: 0.8;
  }

  .newsletter h4 {
    margin-bottom: 1rem;
  }

  .newsletter-form {
    display: flex;
    gap: 0.5rem;
  }

  .newsletter-form input {
    flex: 1;
    padding: 0.8rem;
    border: none;
    border-radius: 25px;
    background: rgba(255,255,255,0.1);
    color: white;
  }

  .newsletter-form input::placeholder {
    color: rgba(255,255,255,0.7);
  }

  .newsletter-form button {
    background: #9cfffa;
    color: #084b83;
    border: none;
    padding: 0.8rem 1.5rem;
    border-radius: 25px;
    cursor: pointer;
    font-weight: 600;
  }

  .footer-bottom {
    text-align: center;
    padding-top: 2rem;
    border-top: 1px solid rgba(255,255,255,0.1);
    color: rgba(255,255,255,0.7);
  }

  /* Animations */
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  /* Responsive Design */
  @media (max-width: 768px) {
    .hero-headline {
      font-size: 2.5rem;
    }

    .hero-subheadline {
      font-size: 1.1rem;
    }

    .confusion-to-clarity {
      flex-direction: column;
      gap: 1.5rem;
    }

    .arrow {
      transform: rotate(90deg);
    }

    section h2 {
      font-size: 2rem;
    }

    .how-it-works {
      grid-template-columns: 1fr;
    }

    .pricing-grid {
      grid-template-columns: 1fr;
    }

    .pricing-card.featured {
      transform: none;
    }

    .footer-content {
      grid-template-columns: 1fr;
      text-align: center;
    }

    .footer-links {
      justify-content: center;
    }

    .newsletter-form {
      flex-direction: column;
    }
  }

  @media (max-width: 480px) {
    .container {
      padding: 0 1rem;
    }

    .hero-content {
      padding: 1rem;
    }

    .hero-headline {
      font-size: 2rem;
    }

    .final-cta-section h2 {
      font-size: 2rem;
    }
  }
</style>
